/** @jsxImportSource @emotion/react */
import * as React from "react";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import { SpinnerBlock } from "../common/Spinner";
import { TimeslotsListRow } from "./TimeslotsListRow";
import { TimeslotTemplateListItem } from "./model/TimeslotTemplatesSuccessResponse";
import TimeslotsListFilters from "./TimeslotsListFilters";
import { Table } from "../../components/table-components/Table";
import { TableCellHeaderText } from "../../components/table-components/TableCellHeaderText";
import { cardWrapperStyle } from "./SalesChannelsList";
import { cardAdditionalStyling } from "./SalesChannelsList";
import { TableRowHeader } from "../../components/table-components/TableRowHeader";
import { CardContainer } from "../../components/page-skeleton/CardContainer";
import { PageHeader } from "../../components/page-skeleton/PageHeader";
import { CardBody } from "../../components/page-skeleton/CardBody";
import { css } from "@emotion/react";
import { EmptyState } from "../../components/EmptyState";
import { Divider } from "../../components/Divider";
import { CopyTimeslotsSelector } from "./CopyTimeslotsSelector";
import { useListTimeslots } from "./service/shop-api";
import { CreateTimeslotsSelector } from "./CreateTimeslotsSelector";

interface Props {
  shopId: string;
}

export interface TimeslotsFormRefHandle {
  submit: () => Promise<any>;
}

const cardBodyEmptyStateAdditionalStyling = css`
  padding-bottom: 24px;
`;

const TimeslotsList = React.forwardRef<TimeslotsFormRefHandle, Props>(
  (props) => {
    const shopId = props.shopId;

    const organization =
      useSelector(authSelectors.selectedOrganizationKeyName) || "";

    const {
      data: timeslotTemplates,
      isLoading: isLoadingShopTimeslotTemplates,
    } = useListTimeslots(organization, shopId);

    const [filters, setFilters] = React.useState({ day: "Monday" });

    if (isLoadingShopTimeslotTemplates) {
      return <SpinnerBlock />;
    }

    return (
      <div css={cardWrapperStyle}>
        <CardContainer css={cardAdditionalStyling}>
          <PageHeader title="Timeslots">
            <CreateTimeslotsSelector />
            <CopyTimeslotsSelector
              wordingType={
                timeslotTemplates && timeslotTemplates.length > 0
                  ? "overwrite"
                  : "import"
              }
            />
          </PageHeader>
          <CardBody additionalStyles={cardBodyEmptyStateAdditionalStyling}>
            <TimeslotsListFilters
              currentFilters={{ day: "Monday" }}
              onSubmit={(values: TimeslotTemplateListItem) => {
                setFilters({ ...filters, ...values });
              }}
            />
          </CardBody>
          {timeslotTemplates && timeslotTemplates.length > 0 ? (
            <Table>
              <thead>
                <TableRowHeader>
                  <TableCellHeaderText>Day</TableCellHeaderText>
                  <TableCellHeaderText>Start</TableCellHeaderText>
                  <TableCellHeaderText>End</TableCellHeaderText>
                  <TableCellHeaderText>Order max</TableCellHeaderText>
                  <TableCellHeaderText>Pickup max</TableCellHeaderText>
                  <TableCellHeaderText>Delivery max</TableCellHeaderText>
                  <TableCellHeaderText>Max units</TableCellHeaderText>
                  <TableCellHeaderText>Prep time</TableCellHeaderText>
                  <TableCellHeaderText>Delivery time</TableCellHeaderText>
                  <TableCellHeaderText>Status</TableCellHeaderText>
                </TableRowHeader>
              </thead>
              <tbody>
                {timeslotTemplates
                  .filter((t) => t.day === filters.day)
                  ?.map(
                    (template: TimeslotTemplateListItem, rowIndex: number) => {
                      return (
                        <TimeslotsListRow
                          key={template.id}
                          template={template}
                          organization={organization}
                          shopId={shopId}
                          rowIndex={rowIndex}
                        />
                      );
                    }
                  )}
              </tbody>
            </Table>
          ) : (
            <>
              <Divider />
              <CardBody>
                <EmptyState
                  title="No timeslots created yet"
                  subtitle="Create timeslots above."
                />
              </CardBody>
            </>
          )}
        </CardContainer>
      </div>
    );
  }
);

export default TimeslotsList;
